import axios from 'axios';
import { installRequestId } from '../utils/requestId';
import { resolveMonolithApiBaseUrl, getMarketplaceDeployOrigin } from './resolveApiBaseUrl';

const API_BASE_URL = resolveMonolithApiBaseUrl([
  'REACT_APP_GIFTCARDS_API_URL',
  'REACT_APP_WALLET_API_URL',
  'REACT_APP_POINTS_API_URL',
  'REACT_APP_API_BASE_URL',
]);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Send cookies with requests (httpOnly cookies)
});
installRequestId(api);

// Request interceptor - cookies are sent automatically by browser
api.interceptors.request.use(
  (config) => {
    // Cookies are automatically sent when withCredentials: true
    // No need to manually add Authorization header
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        // Refresh token is in httpOnly cookie, sent automatically
        // Use same domain for token refresh to ensure cookies are sent
        const accountsApiUrl = process.env.REACT_APP_API_BASE_URL ||
          getMarketplaceDeployOrigin() || 'http://localhost:8001';
        await axios.post(
          `${accountsApiUrl}/api/token/refresh/`,
          {},  // No body needed - refresh token in cookie
          { withCredentials: true }  // Send cookies
        );
        // New tokens are set in httpOnly cookies by backend
        // No need to read response - tokens in cookies automatically
        // Retry original request (cookies will be sent automatically)
        return api(originalRequest);
      } catch (refreshError) {
        // Only redirect to login if it's a persistent auth failure
        // Don't redirect on network errors or temporary issues
        if (refreshError.response?.status === 401 || refreshError.response?.status === 403) {
          // Redirect to login - cookies will be cleared by backend
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export const giftCardsAPI = {
  // Get all gift cards
  getGiftCards: async () => {
    const response = await api.get('/api/wallet/giftcards/');
    return response.data;
  },

  // Get gift card by ID
  getGiftCardById: async (id) => {
    const response = await api.get(`/api/wallet/giftcards/${id}/`);
    return response.data;
  },

  // Transfer gift card — requires both recipient username and phone number
  transferGiftCard: async (code, phoneNumber, username) => {
    const response = await api.post('/api/wallet/giftcards/transfer/', {
      code,
      phone_number: phoneNumber,
      username,
    });
    return response.data;
  },

  // Redeem gift card
  redeemGiftCard: async (code) => {
    const response = await api.post('/api/wallet/giftcards/redeem/', {
      code,
    });
    return response.data;
  },

  // Get gift card notifications
  getNotifications: async () => {
    const response = await api.get('/api/wallet/giftcards/notifications/');
    return response.data;
  },

  // Mark notification as read
  markNotificationRead: async (notificationId) => {
    const response = await api.post('/api/wallet/giftcards/notifications/mark-read/', {
      notification_id: notificationId,
    });
    return response.data;
  },

  // Get gift card status
  getStatus: async () => {
    const response = await api.get('/api/wallet/giftcards/status/');
    return response.data;
  },
};

export default giftCardsAPI;

